/**
 * The 2026-10-02 prod-fix batch copy deck: every user-facing string of UX-SPEC section 13
 * (manual-qa/review8/prod-fixes-2026-10-02/UX-SPEC.md), keyed by its deck ID, verbatim.
 *
 * THE single source for these strings on the web (PLAN.md section 0 "Copy"): components read them from
 * here and never inline them, `copy-deck.prod-fixes.spec.ts` pins every entry against a byte copy of
 * section 13, and the UX-SPEC section 1 never-in-user-copy list (engine/tool/server names, internal codes,
 * paths) is a test over this file. Server-rendered notes (X3-A1/A2/V1/I1/G1/W1/W1b, X6-1/2, X12-*, XS1-1/3,
 * XS2-1, XS3-1, XR-2) are in ironman `UserCopy.kt` too; the card shows the server's text and falls back to
 * this deck only when a structured note arrives without one.
 *
 * Placeholders are `{name}` exactly as the deck writes them: fill them with `fmtCopy`.
 */
export const PROD_FIXES_COPY = {
    // FIX-2 expired uploads
    'X2-1': 'This file is no longer on our servers — uploads are deleted after a few hours to protect your privacy.',
    'X2-2': 'Upload it again',
    'X2-3': "This file is no longer on our servers, so it can't be run again. Upload it again to use these settings.",
    'X2-4': 'This upload expired. Remove it and add the file again.',
    'X2-5': 'Uploading again…',
    'X2-6': 'Your settings from last time are selected.',
    'X2-7': 'Checking…',
    // FIX-3 compressions that do not shrink
    'X3-A1': 'This audio was already about as small as these settings can make it, so we kept your original file unchanged.',
    'X3-A2': "Saved as MP3 inside WAV to make it smaller. If your audio editor won't open it, run it again with PCM 16-bit.",
    'X3-V1': "This video is already well compressed — these settings wouldn't make it smaller, so we kept your original unchanged.",
    'X3-I1': "This image is already well compressed — these settings wouldn't make it smaller, so we kept your original unchanged.",
    'X3-I2': 'Your image is already smaller than the size you asked for, so we kept your original unchanged.',
    'X3-G1': 'This file was already about as small as these settings can make it, so we kept your original unchanged.',
    'X3-W1': 'This is only {n}% smaller than the original. For a smaller file, try a lower quality or a target size.',
    'X3-W1b': 'This is about the same size as the original. For a smaller file, try a lower quality or a target size.',
    'X3-S1': 'Size: {size} (your original, unchanged)',
    'X3-S2': 'New size: {size} ({pct}% of the original {orig})',
    'X3-C1': 'Original kept',
    'X3-B1': 'Make it smaller',
    'X3-B2': "We've picked a stronger setting: {label}. You can change it.",
    'X3-WAV1': 'Auto — smallest file (recommended)',
    'X3-WAV2': 'Auto makes the smallest WAV it can without an audible drop for most listening: it trims extra bit depth when your file has it, otherwise it stores the audio as MP3 inside WAV (up to 10× smaller). Choose a PCM option if your audio editor needs uncompressed WAV.',
    'X3-WAV3': 'Make WAV files smaller and keep the .wav format. Free, no sign-up.',
    // FIX-4 contact form
    'X4-1': 'Please write a message.',
    'X4-2': "That email doesn't look right. Check it, or leave it blank.",
    'X4-3': `Please tick "I'm not a robot" first.`,
    'X4-4': 'Sending…',
    'X4-5': 'Uploading attachment… {n}%',
    'X4-6': 'Thanks — your message was sent.',
    'X4-7': "We'll reply to the email you entered.",
    'X4-8': "You didn't leave an email, so we won't be able to reply.",
    'X4-9': 'Send another message',
    'X4-10': `Your message didn't send. Please tick "I'm not a robot" again and resend, or email us at support@xconvert.com.`,
    'X4-11': "You've sent several messages in a short time. Please wait a few minutes and try again.",
    // FIX-6 Shockwave DCR / vector SWF
    'X6-0': "Can't convert",
    'X6-1': "This is an Adobe Shockwave file, not a Kodak camera photo. We can't convert Shockwave files.",
    'X6-2': "This Flash file is an animation or game drawn in vectors, which we can't convert. We can only convert SWF files that contain video. To play it, try the free Ruffle player at ruffle.rs.",
    // FIX-8 duplicate submissions
    'X8-1': 'Starting…',
    'X8-2': 'View progress',
    'X8-3': 'This file is already converting with these settings. Change a setting to run another version.',
    'X8-4': 'Already converting with these settings. Change a setting to run another version.',
    'X8-5': 'These are the settings you already used.',
    'X8-6': 'Download result',
    'X8-7': 'Run it again anyway',
    'X8-8': 'You converted this file with the same settings {time} ago.',
    'X8-9': 'Download that result',
    'X8-10': 'Convert again',
    // RETRY
    'XR-1': 'Retrying automatically — a hiccup on our side, not your file.',
    'XR-2': 'We already tried this twice.',
    'XR-3': 'Still working — we restarted it automatically after a hiccup.',
    // SMALL-1 frame picker at the end
    'XS1-1': 'The video ends at {t}, so we used its last frame.',
    'XS1-2': 'The video ends at {t}.',
    'XS1-3': "We couldn't read the end of this video. Try a frame a few seconds earlier.",
    // SMALL-2 archive names
    'XS2-1': "Some file names in this archive used characters we couldn't read, so we renamed those files.",
    // SMALL-3 RAW photos
    'XS3-1': "We couldn't read this RAW photo. Some newer phone RAW formats aren't supported yet — try saving the photo as JPEG or HEIC on your phone instead.",
    // UXFU1-05 (fu-r2): the same failure on a camera's RAW file (DCR, NEF, CR2, ...) -- it came from a camera, not a phone.
    'XS3-2': "We couldn't read this RAW photo. Try exporting it as JPEG or TIFF from your camera's software.",
    // SMALL-4 SVG / EPUB failures
    'X12-1': "We couldn't process this SVG — it may be too large or complex. Try a smaller file, or convert it to PNG instead.",
    'X12-2': "Converting this PDF to EPUB took too long and was stopped. Long or image-heavy PDFs can't always be converted — try splitting it into smaller parts first.",
    'X12-3': "We couldn't convert this PDF to EPUB. Try a different PDF, or convert it to another format.",
} as const;

export type ProdFixesCopyId = keyof typeof PROD_FIXES_COPY;

/** The deck string for `id`. */
export function copyOf(id: ProdFixesCopyId): string {
    return PROD_FIXES_COPY[id];
}

/**
 * Fills `{name}` placeholders (`{n}`, `{t}`, `{size}`, `{pct}`, `{orig}`, `{label}`, `{time}`) in a deck
 * string. A placeholder with no value stays visible, so a missing parameter fails a test instead of
 * shipping half a sentence; a value is inserted as text (React escapes it), never as markup.
 */
export function fmtCopy(template: string, values: Record<string, string | number> = {}): string {
    return template.replace(/\{([A-Za-z]+)\}/g, (whole, key: string) =>
        Object.prototype.hasOwnProperty.call(values, key) ? String(values[key]) : whole);
}
